import serializeItemResult from '../qti/serializer/itemResult/ItemResultSerializer';
import { reportError } from './ErrorReporting';
import responseReader from '../store/readHelpers/ResponseReader';
import sessionReader from '../store/readHelpers/SessionReader';
import apiService from './api/ApiService';
import storeManager from '../store/StoreManager';
import apiReader from '../store/readHelpers/ApiReader';
import apiActions from '../actions/ApiActions';
import utilService from '../services/UtilService';
import alertService from '../services/AlertService';
import uploadTokenReader from '../store/readHelpers/UploadTokenReader';
import { t } from './i18nService';
import alertLevel from '../components/alert/AlertLevel';
import navigationService from './NavigationService';
import stateCacheService from './StateCache';
import { responseCategories } from './api/ApiConstants';
import questionSelectionService from './QuestionSelectionService';
import connectionStatusService from './ConnectionStatusService';
import connectionStatusConstants from '../constants/ConnectionStatusConstants';
import responseActions from '../actions/ResponseActions';
import responseService from './ResponseService';
import responseStatusActions from '../actions/ResponseStatusActions';
import marketplacePropertiesService from './MarketplacePropertiesService';
import autoSaveResponseService from './AutoSaveResponseService';

export const defaultInterval = 2000; // Milliseconds
export const waitIntervalsOnError = 20; // Wait 20 * defaultInterval before trying a new StoreUserResponse
const { SAVED, SHORT_DURATION } = connectionStatusConstants;
let instance = null;

/** Outcome of immediatelySendResponse. Only PERSISTED means the server has it. */
export const responseSendStatus = {
    PERSISTED: 'persisted',
    QUEUED: 'queued',
    FAILED: 'failed',
};

export default class ResponseSendingQueue {
    constructor() {
        this.intervalHandle = null;
        this.waitCount = 0;
    }

    resetWaitCount() {
        this.waitCount = 0;
    }

    queueResponse(contentRevisionId) {
        if (!contentRevisionId) {
            reportError(`ResponseSendingQueue: Attempting to queue response with contentRevisionId ${contentRevisionId}`);
            return undefined;
        }
        const currentQueue = apiReader.getUserResponseQueue();
        const currentIndex = currentQueue.findIndex(item => item === contentRevisionId);

        if (currentIndex === -1) {
            const newQueue = currentQueue.slice(0);
            newQueue.push(contentRevisionId);
            storeManager.getStore().dispatch(apiActions.setUserResponseQueue(newQueue));
        }

        return this;
    }

    /**
     * Adds the contentRevisionIds to the queue.
     * @param {array} contentRevisionIds
     */
    queueResponses(contentRevisionIds) {
        contentRevisionIds.forEach((contentRevisionId) => {
            this.queueResponse(contentRevisionId);
        });
    }

    shiftNextContentRevisionIdFromTheQueue() {
        const currentQueue = apiReader.getUserResponseQueue();

        if (currentQueue.length) {
            const newQueue = currentQueue.slice(0);
            const contentRevisionId = newQueue.shift();

            const requestId = `${contentRevisionId}-${new Date().getTime()}`;

            storeManager.getStore().dispatch(apiActions.setUserResponseQueueWithPendingRequest(newQueue, requestId));

            return {
                contentRevisionId,
                requestId,
            };
        }

        return {};
    }

    sendNextResponse() {
        if (this.waitCount > 0) {
            // Previously StoreUserResponse failed and we should wait waitIntervalsOnError intervals before
            // attempting again
            this.waitCount--;
            window.console.log(`${this.waitCount} intervals left before trying another StoreUserResponse`);
            return undefined;
        }

        if (apiReader.hasPendingResponseSendingRequests()) {
            window.console.log('StoreUserResponse in progress, wait until next interval.');
            return undefined;
        }

        if (sessionReader.isPaused()) {
            window.console.log('Assessment run is paused, wait until next interval');
            return undefined;
        }

        const { contentRevisionId, requestId } = this.shiftNextContentRevisionIdFromTheQueue();

        if (!contentRevisionId) {
            if (requestId) {
                reportError(`Invalid value for contentRevisionId: ${contentRevisionId}. Removing pending request.`);
                this.removePendingRequest(requestId);
            }
            return undefined;
        }

        const questionResponse = responseReader.getItemResultForQuestion(contentRevisionId);
        if (!questionResponse) {
            // should not happen, but questionResponse will be undefined if question has not yet been parsed and
            // sending initial item result
            this.removePendingRequest(requestId);
            reportError(`Tried to send item result for ${contentRevisionId} but it was undefined!`);
            return undefined;
        }

        if (responseService.isQuestionCommitAnswerAlreadyStored(questionResponse)) {
            // committed answers must not been committed again with enabled one way navigation
            this.removePendingRequest(requestId);
            return undefined;
        }

        const itemResultXML = serializeItemResult(questionResponse, false);

        const additionalData = {};
        try {
            const uploadToken = uploadTokenReader.getTokenForQuestionId(contentRevisionId);
            if (uploadToken) {
                additionalData.uploadToken = uploadToken;
            }
            const tabToken = sessionReader.getSessionInfoValue('tabToken');
            if (tabToken) {
                additionalData.tabToken = tabToken;
            }

            additionalData.avoidStoring = questionSelectionService.avoidStoringResponse(contentRevisionId);

            if (responseService.isQuestionCommitAnswerReadyToBeStored(questionResponse)) {
                additionalData.commitAnswerTimestamp = questionResponse.commitAnswerTimestamp;
            }
        } catch (e) {
            reportError(`Error setting additional data on item result for ${contentRevisionId}`);
        }

        const store = storeManager.getStore();
        const isMonitorAlertEssayAutoSaveFailedEnabled = marketplacePropertiesService.isMonitorAlertEssayAutoSaveFailedEnabled();
        return apiService.storeUserResponse(itemResultXML, contentRevisionId, additionalData)
            .then((response) => {
                this.removePendingRequest(requestId);
                // success: false is a declined save.
                const stored = Boolean(response?.success);

                if (stored) {
                    window.console.log('StoreUserResponse success');
                    store.dispatch(responseStatusActions.setLastSaved(contentRevisionId, questionResponse.datestamp));
                    store.dispatch(apiActions.setUserResponseQueueStatus(responseCategories.OK, utilService.getIsoDateStamp()));
                    if (responseService.isQuestionCommitAnswerReadyToBeStored(questionResponse)) {
                        store.dispatch(responseActions.setStoredQuestionCommitTimestamp(contentRevisionId));
                    }
                    if (isMonitorAlertEssayAutoSaveFailedEnabled) {
                        autoSaveResponseService.storeUserResponseSuccess(contentRevisionId);
                    }
                    connectionStatusService.addStatusToQueue(SAVED, SHORT_DURATION);
                } else {
                    if (isMonitorAlertEssayAutoSaveFailedEnabled) {
                        autoSaveResponseService.storeUserResponseFail(contentRevisionId);
                    }
                    alertService.showAlert(t('status.saveResponseError'), {
                        html: true,
                        offset: 5,
                        position: 'top-right',
                        customFields: {
                            level: alertLevel.ERROR,
                        },
                    });
                }

                return stored;
            })
            .catch((ex) => {
                this.removePendingRequest(requestId);

                if (ex.errorType === responseCategories.INVALID_TAB_TOKEN) {
                    // We remove the cached response for this question to avoid risking overwriting a newer response on reload
                    const stateCache = stateCacheService.getStateCacheInstance();
                    if (stateCache) {
                        stateCache.removeQuestionResponse(contentRevisionId);
                    }

                    navigationService.navigateToInvalidTabTokenPage();
                } else {
                    this.waitCount = waitIntervalsOnError;
                    window.console.log(`StoreUserResponse failed. Wait ${this.waitCount} intervals before trying again.`);
                    store.dispatch(
                        apiActions.setUserResponseQueueStatus(ex, utilService.getIsoDateStamp()),
                    );

                    this.queueResponse(contentRevisionId);
                    reportError(ex);
                }

                return false;
            });
    }

    startQueue(interval = defaultInterval) {
        this.intervalHandle = setInterval(this.sendNextResponse.bind(this), interval);

        return this;
    }

    removePendingRequest(requestId) {
        const store = storeManager.getStore();
        store.dispatch(
            apiActions.removePendingResponseRequest(requestId)
        );
    }

    stopQueue() {
        if (this.intervalHandle) {
            clearInterval(this.intervalHandle);
        }
    }

    /**
     * @returns {Promise<string>} a responseSendStatus: PERSISTED when the server confirmed it,
     * FAILED when declined, errored or in backoff, QUEUED when another response went first.
     */
    immediatelySendResponse(contentRevisionId) {
        this.queueResponse(contentRevisionId);

        // In error backoff: fail now, but still tick the queue so the backoff counts down.
        if (this.waitCount > 0) {
            this.sendNextResponse();
            return Promise.resolve(responseSendStatus.FAILED);
        }

        // The send only describes this response if it is at the head of the queue.
        const sendsThisResponse = apiReader.getUserResponseQueue()[0] === contentRevisionId;
        const pending = this.sendNextResponse();

        if (!sendsThisResponse) {
            return Promise.resolve(responseSendStatus.QUEUED);
        }

        return Promise.resolve(pending).then((stored) => {
            if (stored === true) {
                return responseSendStatus.PERSISTED;
            }
            // undefined: not sent (paused or a request in flight), still queued.
            return stored === false ? responseSendStatus.FAILED : responseSendStatus.QUEUED;
        });
    }

    static getInstance() {
        if (!instance) {
            instance = new ResponseSendingQueue();
        }

        return instance;
    }
}
